import React, { useState } from 'react';
import { Github, ExternalLink, Lock, Network } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const projects = [
  {
    number: '01',
    title: 'Stealth Messaging App',
    subtitle: 'Real-time encrypted communication',
    description:
      'A high-performance real-time chat application with sub-second message delivery. Built with end-to-end AES encryption, advanced message visibility controls, and a scalable backend architecture designed to handle concurrent users seamlessly.',
    stack: ['NestJS', 'React', 'MongoDB', 'Socket.io', 'WebSockets', 'AES Encryption'],
    github: 'https://github.com/dewmith-akalanka', // placeholder
    live: null,
    icon: Lock,
    accent: '#1a1a18',
    highlights: ['Sub-second message delivery via WebSockets', 'AES end-to-end encryption', 'Advanced message visibility controls', 'Scalable concurrent user architecture'],
  },
  {
    number: '02',
    title: 'Uni Connect',
    subtitle: 'Alumni social networking platform',
    description:
      'A full-featured social networking platform connecting university alumni. Includes RESTful APIs for managing interactions, an analytics dashboard visualizing engagement metrics with ChartJS, and job posting & networking features.',
    stack: ['Node.js (Express)', 'React', 'MongoDB', 'ChartJS', 'REST APIs'],
    github: 'https://github.com/dewmith-akalanka', // placeholder
    live: null,
    icon: Network,
    accent: '#c8602a',
    highlights: ['Alumni social graph & feeds', 'Analytics dashboard with ChartJS', 'Job posting & networking features', 'RESTful API architecture'],
  },
];

function ProjectCard({ project, index, inView }) {
  const [hovered, setHovered] = useState(false);
  const Icon = project.icon;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(32px)',
        transition: `all 0.7s ease ${index * 0.15}s`,
        border: `1px solid ${hovered ? project.accent : 'var(--border)'}`,
        borderRadius: 4,
        overflow: 'hidden',
        cursor: 'default',
        background: hovered ? 'var(--white)' : 'var(--cream)',
        boxShadow: hovered ? '0 20px 60px rgba(0,0,0,0.06)' : 'none',
        transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s, border-color 0.3s, box-shadow 0.3s, background 0.3s`,
      }}>

      {/* Top bar */}
      <div style={{
        height: 3,
        background: project.accent,
        opacity: hovered ? 1 : 0.15,
        transition: 'opacity 0.3s',
      }} />

      <div style={{ padding: 'clamp(28px, 4vw, 48px)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 4,
              background: hovered ? project.accent : 'var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.3s',
              flexShrink: 0,
            }}>
              <Icon size={20} color={hovered ? 'white' : 'var(--ink-3)'} strokeWidth={1.5} />
            </div>
            <div>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                Project {project.number}
              </p>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 2vw, 26px)', letterSpacing: '-0.01em', color: 'var(--ink)', lineHeight: 1.1 }}>
                {project.title}
              </h3>
            </div>
          </div>

          {/* Links */}
          <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
            <a href={project.github} target="_blank" rel="noreferrer"
              title="GitHub Repository"
              style={{ color: 'var(--ink-3)', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = project.accent; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-3)'; }}>
              <Github size={18} strokeWidth={1.5} />
            </a>
            {project.live && (
              <a href={project.live} target="_blank" rel="noreferrer"
                style={{ color: 'var(--ink-3)', transition: 'color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.color = project.accent; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-3)'; }}>
                <ExternalLink size={18} strokeWidth={1.5} />
              </a>
            )}
          </div>
        </div>

        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: hovered ? project.accent : 'var(--ink-4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 16, transition: 'color 0.3s' }}>
          {project.subtitle}
        </p>

        <p style={{ color: 'var(--ink-2)', lineHeight: 1.75, fontWeight: 300, fontSize: 15, marginBottom: 28 }}>
          {project.description}
        </p>

        {/* Highlights */}
        <div style={{ marginBottom: 32, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {project.highlights.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: project.accent, flexShrink: 0, opacity: hovered ? 1 : 0.3, transition: 'opacity 0.3s' }} />
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-2)' }}>{h}</p>
            </div>
          ))}
        </div>

        {/* Stack tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
          {project.stack.map(tag => (
            <span key={tag} style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.05em',
              color: 'var(--ink-3)', padding: '4px 10px',
              border: '1px solid var(--border)', borderRadius: 2,
            }}>{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [ref, inView] = useInView(0.1);

  return (
    <section id="projects" ref={ref} style={{ padding: 'clamp(80px, 10vw, 140px) clamp(24px, 8vw, 120px)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ opacity: inView ? 1 : 0, transform: inView ? 'none' : 'translateY(24px)', transition: 'all 0.6s ease', marginBottom: 60 }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent-warm)', marginBottom: 20 }}>
            03 — Projects
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 20 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 4vw, 52px)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              Things I've built.
            </h2>
            <a href="https://github.com/dewmith-akalanka" target="_blank" rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--ink-2)', border: '1px solid var(--border)', padding: '9px 20px', borderRadius: 2,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.color = 'var(--ink)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-2)'; }}>
              <Github size={14} strokeWidth={1.5} /> View GitHub
            </a>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 480px), 1fr))', gap: 24 }}>
          {projects.map((project, i) => (
            <ProjectCard key={project.title} project={project} index={i} inView={inView} />
          ))}
        </div>

        {/* CTA */}
        <div style={{
          marginTop: 56, padding: '40px 48px', border: '1px solid var(--border)',
          borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 24, background: 'var(--white)',
          opacity: inView ? 1 : 0, transition: 'opacity 0.6s ease 0.4s',
        }}>
          <div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: '-0.01em', marginBottom: 6 }}>More on GitHub</p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-3)' }}>Explore all repositories and contributions</p>
          </div>
          <a href="https://github.com/dewmith-akalanka" target="_blank" rel="noreferrer"
            style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
              background: 'var(--ink)', color: 'var(--cream)', padding: '12px 28px', borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-warm)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--ink)'; }}>
            <Github size={14} strokeWidth={1.5} /> github.com/dewmith-akalanka
          </a>
        </div>
      </div>
    </section>
  );
}
